/**
 * IDoScreen - K Comparison I Do Practice
 *
 * Shows two numbers and highlights the larger one (correct answer).
 * Teacher demonstrates which number is greater.
 *
 * Audio sequence:
 * 1. "Which is greater?" (intro) - while showing both numbers
 * 2. "[X] is greater." (result) - while highlighting correct answer
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { NUMERAL_FONT_STYLE } from '../../../styles/design-system/typography';
import { KPracticeSettings } from '../types';
import { ComparisonFact } from './types';
import { useKTTS, K_QUESTION_SCREEN_AUDIO } from '../../../hooks/useKAudio';

// Use PreK shared components
import {
  PracticeHeader,
  PRACTICE_PHASES,
} from '../../PreKPractice/shared';

interface IDoScreenProps {
  settings: KPracticeSettings;
  factsToPractice: ComparisonFact[];
  onComplete: () => void;
  onBack: () => void;
}

const IDoScreen: React.FC<IDoScreenProps> = ({
  settings,
  factsToPractice,
  onComplete,
  onBack,
}) => {
  const [currentFactIndex, setCurrentFactIndex] = useState(0);
  // Phases: 'waiting' -> 'intro' -> 'revealing' -> 'result' -> 'waiting'
  const [currentPhase, setCurrentPhase] = useState<'waiting' | 'intro' | 'revealing' | 'result'>('waiting');

  // Use ref for timer to avoid dependency issues
  const autoAdvanceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { playTTSWithCallback, isTTSPlaying } = useKTTS();

  const currentFact = factsToPractice[currentFactIndex];

  // Get result audio file for the current fact's result
  const getResultAudio = useCallback((result: number) => {
    const resultAudio = K_QUESTION_SCREEN_AUDIO.comparing.iDoResult;
    return resultAudio[result as keyof typeof resultAudio];
  }, []);

  // Initialize to intro phase when fact changes
  useEffect(() => {
    setCurrentPhase('intro');
  }, [currentFactIndex]);

  // Auto-advance to next fact after delay
  const advanceToNextFact = useCallback(() => {
    if (currentFactIndex < factsToPractice.length - 1) {
      setCurrentFactIndex(prev => prev + 1);
    } else {
      onComplete();
    }
  }, [currentFactIndex, factsToPractice.length, onComplete]);

  // Handle intro phase - play "Which is greater?"
  useEffect(() => {
    if (!currentFact || currentPhase !== 'intro') return;

    if (settings.speechEnabled) {
      playTTSWithCallback(K_QUESTION_SCREEN_AUDIO.comparing.iDoIntro, () => {
        setCurrentPhase('revealing');
      }).catch(() => {
        setCurrentPhase('revealing');
      });
    } else {
      const timer = setTimeout(() => {
        setCurrentPhase('revealing');
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [currentPhase, currentFact, settings.speechEnabled, playTTSWithCallback]);

  // Handle revealing phase - play "[X] is greater."
  useEffect(() => {
    if (!currentFact || currentPhase !== 'revealing') return;

    if (settings.speechEnabled) {
      // Small delay to ensure previous audio is fully done
      const delayTimer = setTimeout(() => {
        const resultAudio = getResultAudio(currentFact.result);
        if (resultAudio) {
          playTTSWithCallback(resultAudio, () => {
            setCurrentPhase('result');
          }).catch(() => {
            setCurrentPhase('result');
          });
        } else {
          setCurrentPhase('result');
        }
      }, 100);
      return () => clearTimeout(delayTimer);
    } else {
      const timer = setTimeout(() => {
        setCurrentPhase('result');
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [currentPhase, currentFact, settings.speechEnabled, playTTSWithCallback, getResultAudio]);

  // Handle result phase - wait then advance
  useEffect(() => {
    if (currentPhase !== 'result') return;

    autoAdvanceTimerRef.current = setTimeout(() => {
      advanceToNextFact();
    }, 2000);

    return () => {
      if (autoAdvanceTimerRef.current) {
        clearTimeout(autoAdvanceTimerRef.current);
      }
    };
  }, [currentPhase, advanceToNextFact]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (autoAdvanceTimerRef.current) {
        clearTimeout(autoAdvanceTimerRef.current);
      }
    };
  }, []);

  if (!currentFact) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-100 via-pink-50 to-blue-100 flex items-center justify-center">
        <div className="text-2xl text-gray-600">Loading...</div>
      </div>
    );
  }

  const isRevealed = currentPhase === 'revealing' || currentPhase === 'result';

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-100 via-pink-50 to-blue-100 flex flex-col">
      <PracticeHeader
        phase={PRACTICE_PHASES.I_DO}
        onBack={onBack}
        progress={
          factsToPractice.length > 0
            ? Math.min((currentFactIndex + 1) / factsToPractice.length, 1)
            : 0
        }
      />

      {/* Main Content Area */}
      <div className="flex-1 flex items-center justify-center px-6 py-6">
        <div className="w-full max-w-md lg:max-w-lg">
          {/* Question prompt */}
          <div className="text-center mb-8">
            <div className="text-2xl lg:text-3xl text-gray-700 font-semibold">
              Which is greater?
            </div>
          </div>

          {/* Two number choices stacked vertically */}
          <div className="flex flex-col gap-6">
            {[currentFact.operand1, currentFact.operand2].map((num, index) => {
              const isCorrect = num === currentFact.result;
              const showAsCorrect = isRevealed && isCorrect;

              return (
                <motion.div
                  key={`${currentFact.factId}-${index}`}
                  initial={{ opacity: 0, x: index === 0 ? -30 : 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  className={`
                    h-32 lg:h-40 rounded-3xl font-bold text-5xl lg:text-6xl shadow-xl
                    flex items-center justify-center transition-all duration-500
                    ${
                      showAsCorrect
                        ? 'bg-green-500 text-white border-4 border-green-700 scale-110'
                        : isRevealed && !isCorrect
                        ? 'bg-gray-300 text-gray-500 border-4 border-gray-400 scale-95 opacity-60'
                        : 'bg-gradient-to-br from-blue-400 to-blue-600 text-white border-4 border-blue-700'
                    }
                  `}
                  style={NUMERAL_FONT_STYLE}
                >
                  {num}
                </motion.div>
              );
            })}
          </div>

          {/* Answer indicator */}
          {isRevealed && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center mt-8"
            >
              <div className="text-xl text-green-600 font-semibold">
                {currentFact.result} is greater!
              </div>
            </motion.div>
          )}
        </div>
      </div>

      {/* Audio Playing Indicator */}
      {isTTSPlaying && (
        <div className="flex justify-center pb-8">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex items-center text-gray-600"
          >
            <div className="animate-pulse mr-2">🔊</div>
            <span>Listen carefully...</span>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default IDoScreen;
